import React, { Fragment, useContext, useState } from "react";
import { useLocation, useHistory } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import { ApiConsumer } from "api/ApiConsumer";
import { API_ROUTES } from "../api-routes";
import * as Sentry from "@sentry/react";
import { Helmet } from "react-helmet";
import Header from "components/header";
import Footer from "components/footer";
const axios = require("axios");

const otpSchema = z.object({
  otp: z
    .string()
    .min(1, "OTP must be at least 1 digits.")
    .max(4, "OTP must be at most 4 digits."),
});

const OneTimePinPage = () => {
  const { state } = useLocation();
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm({ resolver: zodResolver(otpSchema) });
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [submissionError, setSubmissionError] = useState("");
  const history = useHistory();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const { email, token, payload, phoneNo } = state?.state || {};

  const handleNavigateToLogin = () => {
    setFormSubmitted(false);
    history.push("/login");
  };

  let apiCall = axios.create();

  const onSubmit = (data) => {
    setIsSubmitting(true);
    setSubmissionError(""); // Clear any previous errors

    apiCall
      .post(API_ROUTES.AUTHENTICATION.VERIFY_OTP(email), {
        token: token, // Remove template literal
        deviceId: "",
        userType: "Registered",
        otp: data.otp, // Remove template literal
      })
      .then((res) => {
        if (res.status === 200) {
          if (res.data.action.includes("VERIFYOTP")) {
            return ApiConsumer.put(API_ROUTES.AUTHENTICATION.UPDATE(email), {
              ...payload,
              token: token,
            });
          } else {
            throw new Error("Invalid action received");
          }
        }
      })
      .then((updateRes) => {
        console.log("Profile update response:", updateRes);
        setFormSubmitted(true);
      })
      .catch((error) => {
        console.error("OTP verification error:", error);

        // Check if the error response contains the specific message
        const errorMessage =
          error.response?.data?.error || // Check for the specific error message
          error.response?.data?.message || // Fallback to a generic message
          error.message || // Fallback to the error message
          "Invalid OTP. Please try again."; // Default message

        setSubmissionError(errorMessage); // Set the error message to display
        Sentry.captureException(error); // Log the error to Sentry
      })
      .finally(() => {
        setIsSubmitting(false);
      });
  };

  return (
    <Fragment>
      <Header />
      <Helmet>
        <title>Register | Bluu Car Rental</title>
      </Helmet>
      <div className="bg-gray-200">
        {/* Header Section */}
        <div className="w-full bg-gray-200 text-center">
          <div className="container m-auto py-6 max-w-screen-xl">
            <div className="col-span-12">
              <h1 className="text-3xl lg:text-4xl px-4 lg:p-4 text-blue-900 font-bold">
                Confirm OTP
              </h1>
              <p className="text-gray-600 mt-2">
                {`Fill in the OTP sent to ${phoneNo} below to complete your Bluu Car Rental online
                profile. This profile allows you to manage your quotes and
                reservations.`}
              </p>
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="relative bg-gray-200 pb-10 px-4">
          <div className="mx-auto max-w-md">
            {formSubmitted ? (
              <div className="col-span-12">
                <div
                  className="bg-green-100 border-t-4 border-green-300 rounded-b-lg p-6 shadow-sm mb-5"
                  role="alert"
                >
                  <div className="flex">
                    <div>
                      <p>
                        <strong>Congratulations.</strong> You successfully
                        created your Bluu online profile.
                      </p>
                      <button
                        onClick={() => handleNavigateToLogin()}
                        type="button"
                        className="text-blue-900 hover:underline font-medium"
                      >
                        Sign in
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <form onSubmit={handleSubmit(onSubmit)}>
                <div className="grid gap-4 p-6 lg:p-8 rounded-lg bg-white shadow-lg">
                  {/* Email Field */}
                  <div className="col-span-12">
                    <label className="block text-sm font-medium text-gray-700">
                      OTP <span className="text-red-600">*</span>
                    </label>
                    <Controller
                      name="otp"
                      control={control}
                      render={({ field }) => (
                        <input
                          type="tel"
                          className={`style-input w-full mt-1 ${
                            errors.otp ? `border-red-700` : `border-gray-300`
                          }`}
                          {...field}
                        />
                      )}
                    />
                    {errors.otp && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.otp.message}
                      </p>
                    )}

                    {submissionError && (
                      <p className="text-red-500 text-sm mt-1">
                        {submissionError}
                      </p>
                    )}
                    {/* Submit Button */}
                    <div className="mt-4">
                      <button
                        type="submit"
                        disabled={isSubmitting}
                        className={`w-full px-4 py-2.5 border border-transparent rounded-md shadow-sm text-base font-medium text-white transition-colors 
      ${isSubmitting ? "bg-blue-700" : "bg-blue-900 hover:bg-blue-800"}
      flex items-center justify-center space-x-2`}
                      >
                        {isSubmitting ? (
                          <>
                            <svg
                              className="animate-spin h-5 w-5 text-white"
                              xmlns="http://www.w3.org/2000/svg"
                              fill="none"
                              viewBox="0 0 24 24"
                            >
                              <circle
                                className="opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                strokeWidth="4"
                              />
                              <path
                                className="opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                              />
                            </svg>
                            <span>Verifying...</span>
                          </>
                        ) : (
                          <span>Verify OTP</span>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </Fragment>
  );
};

export default OneTimePinPage;
